import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Download, Mail, RefreshCw } from "lucide-react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
} from "@chakra-ui/react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { APIContext } from "../../api/api";
import HelmetLayout from "../../components/HelmetLayout";
import {
  formatISTDateTime,
  istDateKey,
  istRangeStart,
  DAY_MS,
} from "../../lib/admin-time";
import "./appsumo.css";

const labels = {
  purchase: "Purchase",
  redeem: "Redemption",
  activate: "Activation",
  upgrade: "Upgrade",
  downgrade: "Downgrade",
  deactivate: "Deactivation",
  refund: "Refund",
};
const label = (e) =>
  e.is_refund ? "Refund" : labels[e.event_type] || e.event_type;
function Customer({ event }) {
  return event.user_id ? (
    <div className="sumo-customer">
      <Link to={`/users/${encodeURIComponent(event.user_id)}`}>
        {event.name || event.email || event.user_id}
      </Link>
      {event.name && event.email && <small>{event.email}</small>}
      {event.email && (
        <a
          className="sumo-email"
          href={`mailto:${encodeURIComponent(
            event.email
          )}?subject=${encodeURIComponent("Your Grigora AppSumo plan")}`}
        >
          <Mail size={13} /> Email customer
        </a>
      )}
    </div>
  ) : (
    <span className="sumo-muted">Unlinked · buyer unknown</span>
  );
}
function Tier({ event }) {
  const from = event.from_tier ? `Tier ${event.from_tier}` : "Unknown";
  const to = event.to_tier ? `Tier ${event.to_tier}` : "Unknown";
  return (
    <>
      {["upgrade", "downgrade"].includes(event.event_type)
        ? `${from} → ${to}`
        : to}
      {event.campaign && (
        <small>
          {event.campaign === "aug26"
            ? "August 2026"
            : event.campaign === "legacy"
            ? "Legacy"
            : event.campaign}
        </small>
      )}
    </>
  );
}
export default function AppsumoActivity() {
  const { getAppsumoActivity } = useContext(APIContext);
  const [filters, setFilters] = useState(() => ({
    start: istRangeStart(30),
    end: istDateKey(),
    type: "all",
    identity: "all",
    source: "all",
    search: "",
    includeTest: false,
  }));
  const [draft, setDraft] = useState(filters);
  const [offset, setOffset] = useState(0),
    [refresh, setRefresh] = useState(0);
  const [data, setData] = useState(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  const [exporting, setExporting] = useState(false),
    [exportError, setExportError] = useState("");
  const [selected, setSelected] = useState(null),
    [detail, setDetail] = useState(null),
    [detailError, setDetailError] = useState("");
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getAppsumoActivity("", { ...filters, offset })
      .then((r) => {
        if (active) setData(r);
      })
      .catch((e) => {
        if (active)
          setError(
            e.response?.data?.error ||
              "Could not load AppSumo activity. Please retry."
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [getAppsumoActivity, filters, offset, refresh]);
  useEffect(() => {
    if (!selected) return;
    let active = true;
    setDetail(null);
    setDetailError("");
    getAppsumoActivity(`/events/${selected}`)
      .then((r) => {
        if (active) setDetail(r);
      })
      .catch(() => {
        if (active) setDetailError("Could not load event details.");
      });
    return () => {
      active = false;
    };
  }, [getAppsumoActivity, selected]);
  const trend = useMemo(() => {
    const days = new Map((data?.trend || []).map((d) => [d.day, d]));
    const rows = [],
      start = Date.parse(filters.start),
      end = Date.parse(filters.end);
    for (let time = start; time <= end && rows.length <= 366; time += DAY_MS) {
      const day = new Date(time).toISOString().slice(0, 10);
      rows.push(
        days.get(day) || {
          day,
          purchases: 0,
          upgrades: 0,
          downgrades: 0,
          refunds: 0,
        }
      );
    }
    return rows;
  }, [data, filters.start, filters.end]);
  async function download() {
    setExporting(true);
    setExportError("");
    try {
      const blob = await getAppsumoActivity("/export", filters, true);
      const url = URL.createObjectURL(blob),
        a = document.createElement("a");
      a.href = url;
      a.download = `appsumo-${filters.start}-${filters.end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (_) {
      setExportError(
        "Export failed. Try a smaller date range (maximum 10,000 events)."
      );
    } finally {
      setExporting(false);
    }
  }
  const update = (key, value) => setDraft((f) => ({ ...f, [key]: value }));
  const apply = (e) => {
    e.preventDefault();
    setOffset(0);
    setFilters({ ...draft });
  };
  const summary = data?.summary || {};
  return (
    <main className="sumo-page">
      <HelmetLayout title="AppSumo Activity" />
      <header className="sumo-header">
        <div>
          <p className="sumo-eyebrow">CUSTOMER LIFECYCLE</p>
          <h1>
            <Activity /> AppSumo activity
          </h1>
          <p>
            Follow purchases, redemptions and plan changes from the first event
            to the latest tier.
          </p>
        </div>
        <div className="sumo-actions">
          <button onClick={() => setRefresh((v) => v + 1)} disabled={loading}>
            <RefreshCw size={16} /> Refresh
          </button>
          <button
            className="sumo-primary"
            onClick={download}
            disabled={exporting || loading || !!error}
          >
            <Download size={16} /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </header>
      <p className="sumo-note">
        Buyers remain unknown until they redeem a license in Grigora. Earlier
        events then link to their account, including across license key changes.
        History starts when logging is enabled; earlier events are unavailable.
      </p>
      <form className="sumo-filters sumo-card" onSubmit={apply}>
        <label>
          From (IST)
          <input
            type="date"
            required
            value={draft.start}
            onChange={(e) => update("start", e.target.value)}
          />
        </label>
        <label>
          Through (IST)
          <input
            type="date"
            required
            min={draft.start}
            value={draft.end}
            onChange={(e) => update("end", e.target.value)}
          />
        </label>
        <label>
          Activity
          <select
            value={draft.type}
            onChange={(e) => update("type", e.target.value)}
          >
            <option value="all">All events</option>
            {Object.entries(labels).map(([value, text]) => (
              <option key={value} value={value}>
                {text}
              </option>
            ))}
            <option value="other">Other events</option>
          </select>
        </label>
        <label>
          Customer
          <select
            value={draft.identity}
            onChange={(e) => update("identity", e.target.value)}
          >
            <option value="all">All customers</option>
            <option value="linked">Linked to a user</option>
            <option value="unlinked">Buyer unknown</option>
          </select>
        </label>
        <label>
          Source
          <select
            value={draft.source}
            onChange={(e) => update("source", e.target.value)}
          >
            <option value="all">All sources</option>
            <option value="webhook">AppSumo webhook</option>
            <option value="redemption">Grigora redemption</option>
          </select>
        </label>
        <label className="sumo-search">
          Search
          <input
            placeholder="Email, name, user ID or license key"
            value={draft.search}
            maxLength={256}
            onChange={(e) => update("search", e.target.value)}
          />
        </label>
        <label className="sumo-check">
          <input
            type="checkbox"
            checked={draft.includeTest}
            onChange={(e) => update("includeTest", e.target.checked)}
          />{" "}
          Include test events
        </label>
        <button className="sumo-primary" type="submit">
          Apply filters
        </button>
      </form>
      {exportError && (
        <p role="alert" className="sumo-error">
          {exportError}
        </p>
      )}
      {error ? (
        <p role="alert" className="sumo-error">
          {error}
        </p>
      ) : loading ? (
        <p role="status">Loading AppSumo activity…</p>
      ) : (
        <>
          <div className="sumo-stats">
            {[
              ["Purchases", "purchases"],
              ["Redemptions", "redemptions"],
              ["Upgrades", "upgrades"],
              ["Downgrades", "downgrades"],
              ["Refunds reported", "refunds"],
              ["Total events", "events"],
            ].map(([text, key]) => (
              <div className="sumo-card" key={key}>
                <span>{text}</span>
                <strong>{(summary[key] || 0).toLocaleString("en-IN")}</strong>
              </div>
            ))}
          </div>
          <section
            className="sumo-card sumo-chart"
            aria-label="AppSumo event trend"
          >
            <h2>Activity over time</h2>
            <p>
              Received events in the selected filters · India time · retries
              counted once
            </p>
            {summary.events ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="day"
                    tickFormatter={(d) => d.slice(5)}
                    minTickGap={24}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar name="Purchases" dataKey="purchases" fill="#623ce7" />
                  <Bar name="Upgrades" dataKey="upgrades" fill="#16a34a" />
                  <Bar name="Downgrades" dataKey="downgrades" fill="#d97706" />
                  <Bar
                    name="Refunds reported"
                    dataKey="refunds"
                    fill="#dc2626"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="sumo-empty">No activity in this period.</p>
            )}
          </section>
          <section className="sumo-card">
            <div className="sumo-table-heading">
              <h2>Event log</h2>
              <span>{summary.unlinked || 0} events with an unknown buyer</span>
            </div>
            <div className="sumo-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Received (IST)</th>
                    <th>Event / reason</th>
                    <th>Customer</th>
                    <th>Tier / campaign</th>
                    <th>License key</th>
                    <th>Source</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.events || []).map((event) => (
                    <tr key={event.id}>
                      <td>{formatISTDateTime(event.received_at)}</td>
                      <td>
                        <span
                          className={`sumo-badge ${
                            event.is_refund ? "refund" : event.event_type
                          }`}
                        >
                          {label(event)}
                        </span>
                        {event.is_test && <small>Test event</small>}
                        <small>{event.reason || "No reason supplied"}</small>
                      </td>
                      <td>
                        <Customer event={event} />
                      </td>
                      <td>
                        <Tier event={event} />
                      </td>
                      <td>
                        <code>{event.license_key}</code>
                        {event.prev_license_key && (
                          <small>
                            Previous: <code>{event.prev_license_key}</code>
                          </small>
                        )}
                      </td>
                      <td>
                        <span>
                          {event.source === "redemption"
                            ? "Grigora redemption"
                            : "AppSumo webhook"}
                        </span>
                        <small>
                          {event.delivery_count}{" "}
                          {event.delivery_count === 1 ? "receipt" : "receipts"}
                        </small>
                      </td>
                      <td>
                        <button
                          onClick={() => setSelected(event.id)}
                          aria-label={`View ${label(event)} event ${event.id}`}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!data?.events.length && (
                <p className="sumo-empty">No events match these filters.</p>
              )}
            </div>
            <footer className="sumo-pagination">
              <span>{data?.total || 0} events · newest received first</span>
              <div>
                <button
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - 50))}
                >
                  Previous
                </button>
                <span>Page {Math.floor(offset / 50) + 1}</span>
                <button
                  disabled={offset + 50 >= (data?.total || 0)}
                  onClick={() => setOffset(offset + 50)}
                >
                  Next
                </button>
              </div>
            </footer>
          </section>
          <p className="sumo-note">
            “Refunds reported” means AppSumo supplied a refund reason. Other
            deactivations may reflect a tier replacement or staff action. Counts
            show received provider events, not confirmation that subscription
            changes succeeded. Redemption events are logged after the
            subscription is saved.
          </p>
        </>
      )}
      <Modal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>AppSumo event details</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {detailError ? (
              <p role="alert">{detailError}</p>
            ) : !detail ? (
              <p role="status">Loading event…</p>
            ) : (
              <div className="sumo-detail">
                <h2>
                  {label(detail.event)} ·{" "}
                  {formatISTDateTime(detail.event.received_at)}
                </h2>
                <Customer event={detail.event} />
                <p>
                  <Tier event={detail.event} />
                </p>
                <h3>License history</h3>
                <p>
                  Latest 100 events across linked license keys. Earlier account
                  associations are preserved after transfers.
                </p>
                <ol>
                  {detail.history.map((e) => (
                    <li key={e.id}>
                      <button onClick={() => setSelected(e.id)}>
                        {label(e)} · {formatISTDateTime(e.received_at)}
                      </button>
                      <span>
                        {" "}
                        · <Tier event={e} /> ·{" "}
                        {e.email || e.user_id || "Buyer unknown"}
                      </span>
                    </li>
                  ))}
                </ol>
                <h3>Original event payload</h3>
                <pre>{JSON.stringify(detail.event.payload, null, 2)}</pre>
                <h3>Receipts</h3>
                <p>
                  {detail.event.delivery_count} receipt(s). Last received{" "}
                  {formatISTDateTime(detail.event.last_received_at)}.
                </p>
                {detail.event.delivery_count > 1 && (
                  <details>
                    <summary>Latest payload</summary>
                    <pre>
                      {JSON.stringify(detail.event.last_payload, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </main>
  );
}
